import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { filterWalletTransactions } from "../../../../../redux/walletTransaction/walletTransactionActions";
import Empty from "../../../../../assets/images/emptyState/payment.svg";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/solid";

function classNames(...classes) {
    return classes.filter(Boolean).join(" ");
}

export default function WalletTransactions({ selectedWallet }) {
    const dispatch = useDispatch();
    const { filterWalletTransactions: transactions, loading } = useSelector((state) => state.walletTransaction);
    const { user } = useSelector((state) => state.auth);
    const [activeTab, setActiveTab] = useState("all");

    useEffect(() => {
        if (selectedWallet && user?.id) {
            dispatch(
                filterWalletTransactions({
                    user_id: user.id,
                    wallet_id: selectedWallet.id,
                })
            );
        }
    }, [selectedWallet, user, dispatch]);

    const getFilteredTransactions = () => {
        if (!transactions || transactions.length === 0) return [];
        
        if (activeTab === "all") return transactions;
        if (activeTab === "deposits") {
            return transactions.filter((t) => t.type?.toLowerCase() === "deposit");
        }
        if (activeTab === "withdrawals") {
            return transactions.filter((t) => t.type?.toLowerCase() === "withdrawal");
        }
        return transactions;
    };

    const groupTransactionsByMonth = (transactions) => {
        const grouped = {};
        transactions.forEach((transaction) => {
            const date = new Date(transaction.created_at);
            const monthYear = date.toLocaleString("en-US", {
                month: "long",
                year: "numeric",
            });
            if (!grouped[monthYear]) {
                grouped[monthYear] = [];
            }
            grouped[monthYear].push(transaction);
        });
        return grouped;
    };

    const formatTime = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
        });
    };

    const formatAmount = (amount, currencyCode) => {
        return `${currencyCode}${parseFloat(amount).toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })}`;
    };

    const filteredTransactions = getFilteredTransactions();
    const groupedTransactions = groupTransactionsByMonth(filteredTransactions);

    return (
        <div className="w-full pb-6">
            {/* Transaction Tabs */}
            <div className="border-b border-gray-200 dark:border-gray-700 mb-4">
                <nav className="-mb-px flex justify-center space-x-8">
                    {[
                        { name: "All", key: "all" },
                        { name: "Deposits", key: "deposits" },
                        { name: "Withdrawals", key: "withdrawals" },
                    ].map((tab) => (
                        <button
                            key={tab.key}
                            onClick={() => setActiveTab(tab.key)}
                            className={classNames(
                                tab.key === activeTab
                                    ? "border-primary text-primary font-semibold"
                                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300",
                                "whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors"
                            )}
                        >
                            {tab.name}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Transactions List */}
            {loading ? (
                <div className="flex justify-center items-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
            ) : filteredTransactions.length === 0 ? (
                <div className="my-10 text-center">
                    <img
                        src={Empty}
                        alt="empty state"
                        className="flex justify-center mx-auto mb-4 w-32 h-32"
                    />
                    <p className="font-bold mb-2 text-gray-900 dark:text-white">No Transactions</p>
                    <p className="text-sm text-gray-400">
                        You don't have any transactions yet.
                    </p>
                </div>
            ) : (
                <div className="space-y-6">
                    {Object.entries(groupedTransactions).map(([monthYear, transactions]) => (
                        <div key={monthYear}>
                            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                                {monthYear}
                            </h3>
                            <div className="space-y-2">
                                {transactions.map((transaction) => (
                                    <div
                                        key={transaction.id}
                                        className="flex items-center justify-between py-3 px-4 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                    >
                                        <div className="flex-1">
                                            <p className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                                                {transaction.type || "Transaction"}
                                            </p>
                                        </div>
                                        <div className="flex-1 text-center">
                                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                                {formatTime(transaction.created_at)}
                                            </p>
                                        </div>
                                        <div className="flex-1 text-center">
                                            <span
                                                className={classNames(
                                                    "inline-flex items-center px-3 py-1 rounded-full text-xs font-medium",
                                                    transaction.status?.toLowerCase() === "successful" ||
                                                        transaction.status?.toLowerCase() === "success"
                                                        ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                                                        : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                                                )}
                                            >
                                                {transaction.status?.toLowerCase() === "successful" ||
                                                transaction.status?.toLowerCase() === "success" ? (
                                                    <CheckCircleIcon className="w-4 h-4 mr-1" />
                                                ) : (
                                                    <XCircleIcon className="w-4 h-4 mr-1" />
                                                )}
                                                {transaction.status || "Pending"}
                                            </span>
                                        </div>
                                        <div className="flex-1 text-right">
                                            <p className="text-sm font-bold text-gray-900 dark:text-white">
                                                {formatAmount(transaction.amount, selectedWallet?.currency_code)}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
